import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Search, CornerDownLeft } from 'lucide-react';
import useCommandPaletteHotkey from './useCommandPaletteHotkey';
import styles from './CommandPalette.module.scss';

/* ────────────────────────────────────────────────────────────
 * CommandPalette — app-wide Cmd/Ctrl+K launcher.
 *
 *   <CommandPalette
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     items={[
 *       { id: 'stock',    label: 'Stock',     group: 'Inventory', onSelect: () => nav('/Stock') },
 *       { id: 'menu',     label: 'Base menu', group: 'Inventory', onSelect: () => nav('/BaseMenu') },
 *       { id: 'people',   label: 'People',    group: 'Teams',     onSelect: () => nav('/Employee') },
 *     ]}
 *   />
 *
 *   const [open, setOpen] = useState(false);
 *   useCommandPaletteHotkey(() => setOpen((o) => !o));
 *
 * Keyboard: ArrowUp/Down to traverse, Enter to fire, Esc to close.
 * ──────────────────────────────────────────────────────────── */

const normalize = (s) => (s || '').toString().toLowerCase();

const CommandPalette = ({ open, onClose, items, placeholder, emptyMessage, className }) => {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // Defer focus to next tick so the portal node is in the DOM.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Body scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const filtered = useMemo(() => {
    const q = normalize(query).trim();
    if (!q) return items;
    return items.filter((it) =>
      normalize(it.label).includes(q) ||
      normalize(it.group).includes(q) ||
      (it.keywords || []).some((k) => normalize(k).includes(q))
    );
  }, [items, query]);

  // Group items in insertion order — preserves the consumer's
  // intentional grouping rather than re-sorting alphabetically.
  const grouped = useMemo(() => {
    const groups = [];
    const indexByGroup = new Map();
    filtered.forEach((it) => {
      const key = it.group || '';
      if (!indexByGroup.has(key)) {
        indexByGroup.set(key, groups.length);
        groups.push({ group: key, items: [] });
      }
      groups[indexByGroup.get(key)].items.push(it);
    });
    return groups;
  }, [filtered]);

  const flatItems = filtered;

  const fire = useCallback(
    (idx) => {
      const it = flatItems[idx];
      if (!it || it.disabled) return;
      it.onSelect?.(it);
      onClose();
    },
    [flatItems, onClose]
  );

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(flatItems.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      fire(activeIdx);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Scroll active row into view as user navigates.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  if (!open) return null;

  const cls = [styles.root, className || ''].filter(Boolean).join(' ');

  const node = (
    <div className={cls} role="presentation">
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div
        className={styles.surface}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div className={styles.searchRow}>
          <Search size={18} strokeWidth={1.75} className={styles.searchIcon} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            placeholder={placeholder}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={onKey}
            className={styles.search}
            aria-label="Search commands"
          />
          <kbd className={styles.kbd}>esc</kbd>
        </div>

        <div className={styles.list} ref={listRef}>
          {grouped.length === 0 && (
            <div className={styles.empty}>{emptyMessage}</div>
          )}
          {grouped.map((g) => (
            <div key={g.group || '_'} className={styles.group}>
              {g.group && <div className={styles.groupLabel}>{g.group}</div>}
              {g.items.map((it) => {
                const idx = flatItems.indexOf(it);
                const active = idx === activeIdx;
                return (
                  <button
                    key={it.id}
                    type="button"
                    data-cmd-idx={idx}
                    disabled={it.disabled}
                    className={[styles.item, active ? styles.itemActive : '']
                      .filter(Boolean)
                      .join(' ')}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => fire(idx)}
                  >
                    {it.icon && <span className={styles.itemIcon}>{it.icon}</span>}
                    <span className={styles.itemLabel}>{it.label}</span>
                    {it.hint && <span className={styles.itemHint}>{it.hint}</span>}
                    {active && (
                      <span className={styles.enter} aria-hidden="true">
                        <CornerDownLeft size={14} strokeWidth={1.75} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};

CommandPalette.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      group: PropTypes.string,
      icon: PropTypes.node,
      hint: PropTypes.node,
      keywords: PropTypes.arrayOf(PropTypes.string),
      disabled: PropTypes.bool,
      onSelect: PropTypes.func,
    })
  ),
  placeholder: PropTypes.string,
  emptyMessage: PropTypes.node,
  className: PropTypes.string,
};

CommandPalette.defaultProps = {
  open: false,
  items: [],
  placeholder: 'Search commands…',
  emptyMessage: 'No matches.',
  className: '',
};

export { useCommandPaletteHotkey };
export default CommandPalette;
